import { useEffect, useMemo, useState } from "react"
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react"
import { InfoModal } from "../portfolio/InfoModal"
import { Button } from "../ui"
import { useLocalization } from "../../lib/i18n"

export type TutorialSlide = {
  title: string
  body: string
  image?: string
}

type HomeTutorialModalProps = {
  isOpen: boolean
  onClose: () => void
  title?: string
  slides?: TutorialSlide[]
  hideImages?: boolean
}

const DEFAULT_SLIDES: TutorialSlide[] = [
  {
    title: "Join or Create a Pool",
    body: `Get in the game by **entering an invite code**, or become the **Commissioner** and create your own pool.

Since you trade against real people, **liquidity is king** - invite everyone you can.`,
    image: "/img/tutorial/1_join_pool.jpg",
  },
  {
    title: "Forecast the Outcome",
    body: "Before you trade, **build your bracket prediction**. The system calculates the *theoretical point value* for every team based on your scenarios. Use those projections to **find undervalued teams** and plan your strategy.",
    image: "/img/tutorial/2_predictions.jpg",
  },
  {
    title: "Points, Not Cash",
    body: `We play with **virtual points**, not real money. You are trading *Futures*, which are simply bets on a team's playoff performance.

Every team will finish with a value between **0 and 100**. Your goal is to **buy low** (e.g., \`20\`) before a team rises, or **sell high** (e.g., \`80\`) before a team crashes. The difference between your price and the result is your profit or loss.`,
    image: "/img/tutorial/3_money_explained.jpg",
  },
  {
    title: "Manage Your Portfolio",
    body: "Track your **active positions** and **open orders** in real-time. As teams advance or get eliminated, their settlement value changes instantly. React quickly to **lock in profits** or **stop your losses**.",
    image: "/img/tutorial/4_portfolio.jpg",
  },
  {
    title: "Master the Market",
    body: "Sports futures trading runs deep. If you get stuck, look for the **'How To' button** on specific pages for detailed guides on *scoring rules*, *order types*, and *settlement logic*.",
    image: "/img/tutorial/5_help.jpg",
  },
]

export function HomeTutorialModal({
  isOpen,
  onClose,
  title,
  slides: providedSlides,
  hideImages = false,
}: HomeTutorialModalProps) {
  const { t } = useLocalization()
  const slides = useMemo(
    () => (providedSlides && providedSlides.length > 0 ? providedSlides : DEFAULT_SLIDES),
    [providedSlides]
  )
  const resolvedTitle = title ?? t("home.tutorial.modalTitle")
  const [index, setIndex] = useState(0)
  const [imageError, setImageError] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIndex(0)
    }
  }, [isOpen, slides])

  useEffect(() => {
    setImageError(false)
  }, [index])

  const next = () => setIndex((prev) => (prev + 1) % slides.length)
  const prev = () => setIndex((prev) => (prev - 1 + slides.length) % slides.length)
  const current = slides[index]
  const renderMarkdown = (text: string) => {
    // Minimal markdown: **bold**, *italic*, `code`, and line breaks
    const escapeHtml = (value: string) =>
      value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    let html = escapeHtml(text)
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    html = html.replace(/\*(.+?)\*/g, "<em>$1</em>")
    html = html.replace(/`(.+?)`/g, "<code>$1</code>")
    html = html.replace(/\n/g, "<br />")
    return { __html: html }
  }

  return (
    <InfoModal isOpen={isOpen} onClose={onClose} title={current?.title ?? resolvedTitle}>
      <div className="space-y-4">
        {!hideImages && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-background-elevated">
            {current.image && !imageError ? (
              <img
                src={current.image}
                alt={current.title}
                className="h-56 w-full object-cover object-top bg-background-surface"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="h-48 w-full bg-gradient-to-r from-brand/20 to-brand/5" />
            )}
          </div>
        )}
        <div>
          <div className="flex items-center justify-end">
            <span className="text-xs text-foreground-muted">
              {t("home.tutorial.stepLabel", { step: index + 1, total: slides.length })}
            </span>
          </div>
          <p
            className="text-body-sm text-foreground-subtle mt-1"
            dangerouslySetInnerHTML={renderMarkdown(current.body)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                className={`h-2.5 w-2.5 rounded-full border border-border-subtle transition ${
                  i === index ? "bg-brand border-brand" : "bg-background"
                }`}
                aria-label={`Go to tutorial slide ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={prev}
              aria-label={t("home.tutorial.previousAccessibility")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={next}
              aria-label={t("home.tutorial.nextAccessibility")}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </InfoModal>
  )
}
